/**
 * Service Connection Status Hook
 *
 * Derives the actual operational status by combining WebSocket connectivity
 * with position data freshness. The WebSocket stays connected to the backend
 * even when the edge device is down — this hook detects that scenario by
 * checking whether position data is actually flowing.
 *
 * States:
 *  - online:       WebSocket connected AND position data arriving recently
 *  - offline:      WebSocket connected but NO position data (edge device down)
 *  - disconnected: WebSocket not connected (backend down)
 *  - connecting:   WebSocket is establishing connection
 */

import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { webSocketConnectionAtom, positionStatsAtom } from '@/stores/atoms';

export type ServiceStatus = 'online' | 'offline' | 'disconnected' | 'connecting';

/** Position data older than this is considered stale (edge likely down). */
const STALE_THRESHOLD_MS = 30_000; // 30 seconds

/** Grace period after connecting before marking as offline. */
const GRACE_PERIOD_MS = 30_000; // 30 seconds

/** How often to re-evaluate freshness. */
const TICK_INTERVAL_MS = 5_000; // 5 seconds

export function useServiceConnectionStatus(): ServiceStatus {
  const connection = useAtomValue(webSocketConnectionAtom);
  const stats = useAtomValue(positionStatsAtom);
  const [now, setNow] = useState(() => Date.now());

  // Periodic tick so staleness is detected even when no atoms change
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return (): void => clearInterval(id);
  }, []);

  if (connection.status === 'connecting') {
    return 'connecting';
  }
  if (connection.status !== 'connected') {
    return 'disconnected';
  }

  // WebSocket is connected — check if position data is actually flowing
  const lastUpdate = stats.lastUpdateTime?.getTime();

  if (!lastUpdate) {
    // No data received yet — show 'connecting' during grace period, then 'offline'
    const connectedAt = connection.lastConnected?.getTime();
    if (connectedAt && now - connectedAt <= GRACE_PERIOD_MS) {
      return 'connecting';
    }
    return 'offline';
  }

  if (now - lastUpdate > STALE_THRESHOLD_MS) {
    return 'offline';
  }
  return 'online';
}
